import React, { JSX, useState } from "react";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { CodeEditor } from "components/common/CodeEditor";
import { ExpandInDialog } from "components/common/ExpandInDialog";
import { SectionRule } from "components/common/SectionRule";
import { TabToggle, type TabToggleOption } from "components/common/TabToggle";
import { Stack } from "components/common/Stack";
import { StatusChip } from "components/common/StatusChip";
import type { RecipeDetail } from "./recipesApi";

const formatDay = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

type SqlView = "definition" | "defaults";

const SQL_VIEWS: readonly TabToggleOption<SqlView>[] = [
  { value: "definition", label: "Definition" },
  { value: "defaults", label: "At defaults" },
];

interface RecipeSummaryProps {
  detail: RecipeDetail;
  /**
   * Input tables to mark "not released" on top of the recipe's own
   * `unreleased_inputs` — the run overlay adds a reopened result's
   * read-ahead sources, which the catalog has no result to know about.
   */
  alsoUnreleased?: ReadonlySet<string>;
  /**
   * The statement to show in place of the stored default — the run overlay's
   * live plan for the picks on screen. Absent leaves the host's own choice of
   * definition-versus-defaults in charge.
   */
  liveSql?: string | null;
  /** Caption for `liveSql`, e.g. while a re-plan is in flight. */
  liveLabel?: string;
}

/**
 * What a recipe IS, independent of running it: description, who wrote it and
 * when, the tables it reads, and the definition rendered to current names.
 *
 * Shared by both overlays so the catalog cannot describe a recipe differently
 * from the canvas. Everything here is read-only by nature — the controls, the
 * result name and the Run button are the run overlay's alone.
 */
export function RecipeSummary({
  detail,
  alsoUnreleased,
  liveSql,
  liveLabel,
}: RecipeSummaryProps): JSX.Element {
  // A recipe with slots is a family of statements; the stored one is the
  // default combination. Show the family first and keep the concrete statement
  // a click away — except where the host is already showing the statement for
  // the picks on screen, which needs no switch.
  const [family, setFamily] = useState(true);
  const notReleasedIds = new Set([
    ...(alsoUnreleased ?? []),
    ...detail.unreleased_inputs.map((table) => table.id),
  ]);
  const generic = detail.sql_generic;
  const shown = liveSql ?? (generic && family ? generic : detail.sql);
  return (
    <Stack gap="lg" className="min-w-0">
      <Stack gap="xs">
        <p className={detail.description ? "" : "text-fg-muted"}>
          {detail.description ?? "No description."}
        </p>
        <p className="text-small text-fg-muted">
          {detail.author.is_me
            ? "Created by you"
            : `Created by ${detail.author.name ?? "someone no longer here"}`}
          {" · "}
          {formatDay(detail.created_at)}
          {detail.version > 1 &&
            ` · version ${detail.version}, ${formatDay(detail.authored_at)}`}
        </p>
      </Stack>
      <Stack gap="xs">
        <SectionRule label="Reads" count={detail.input_tables.length} />
        <ul className="list-none">
          {detail.input_tables.map((table) => (
            <li key={table.id} className="flex items-center gap-2 py-0.5">
              <TableChartOutlinedIcon className="icon-sm text-fg-muted" />
              <span
                className={`truncate ${table.name ? "" : "text-fg-muted font-mono"}`}
              >
                {table.name ?? table.id}
              </span>
              {notReleasedIds.has(table.id) && (
                <StatusChip tone="warning">not released</StatusChip>
              )}
            </li>
          ))}
        </ul>
        {detail.unlinked_joins.length > 0 && (
          <p className="text-small text-fg-muted">
            Joins without a declared link: {detail.unlinked_joins.join("; ")}
          </p>
        )}
      </Stack>
      <Stack gap="xs">
        <SectionRule
          label="SQL"
          trailing={liveLabel}
          // A mode switch, not a caption: `action` renders uppercase mono, which
          // read as a label rather than something to press.
          end={
            liveSql || !generic ? undefined : (
              <span data-testid="recipe-sql-view">
                <TabToggle<SqlView>
                  value={family ? "definition" : "defaults"}
                  onChange={(v) => setFamily(v === "definition")}
                  ariaLabel="SQL view"
                  options={SQL_VIEWS}
                />
              </span>
            )
          }
        />
        <ExpandInDialog label="SQL">
          {(expanded) => (
            <CodeEditor
              value={shown}
              // Only the family view has holes to draw; the concrete statement's
              // `$name` is real SQL and stays as it reads.
              slotTokens={shown === generic}
              onChange={() => {}}
              language="sql"
              readOnly
              lineNumbers={false}
              lineWrapping={false}
              height={expanded ? "fill" : "auto"}
              maxHeight={192}
            />
          )}
        </ExpandInDialog>
      </Stack>
    </Stack>
  );
}
